import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/components/ui/use-toast';
import { API_BASE_URL, ENABLE_LOCAL_FALLBACK } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { getJsonAuthHeaders } from '@/lib/apiHeaders';
import { backupService } from '@/services/backupService';
import { storage } from '@/services/storage';
import { Database, DownloadCloud, FolderIcon, GithubIcon, HardDriveIcon, InboxIcon, Loader2, RefreshCw, ServerIcon, UploadCloud } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

// Flag to indicate if we're in a browser environment
const isBrowser = typeof window !== 'undefined' && typeof window.document !== 'undefined';

// Browser-safe imports for filesystem operations
let fs: any = null;
let path: any = null;

// Only try to import fs and path if we're not in a browser
if (!isBrowser) {
    try {
        // Dynamic imports would work best but are tricky with React
        // Using require as a fallback if dynamic imports fail
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const fsModule = require('fs');
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const pathModule = require('path');
        fs = fsModule;
        path = pathModule;
    } catch (e) {
        console.error('Error importing Node.js modules:', e);
    }
}

const Debug = () => {
    const { toast } = useToast();
    const { user } = useAuth();
    const [storageItems, setStorageItems] = useState<{ key: string; value: any }[]>([]);
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [importProgress, setImportProgress] = useState<number>(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [backendStatus, setBackendStatus] = useState<{ available: boolean, lastChecked: Date | null }>({
        available: false,
        lastChecked: null
    });
    const [isCheckingBackend, setIsCheckingBackend] = useState<boolean>(false);

    useEffect(() => {
        const items = Object.keys(storage).map(key => ({
            key,
            value: storage.getItem(key)
        }));
        setStorageItems(items);
    }, []);

    const handleClearStorage = () => {
        storage.clear();
        setStorageItems([]);
    };

    const handleExportData = () => {
        const data = Object.fromEntries(
            Object.keys(storage).map(key => [key, storage.getItem(key)])
        );
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'storage-backup.json';
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleImportClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target?.result as string);
                Object.entries(data).forEach(([key, value]) => {
                    storage.setItem(key, value as string);
                });
                setStorageItems(Object.keys(storage).map(key => ({
                    key,
                    value: storage.getItem(key)
                })));
            } catch (error) {
                console.error('Error importing data:', error);
            }
        };
        reader.readAsText(file);
    };

    // Function to check backend health
    const checkBackendHealth = async () => {
        setIsCheckingBackend(true);
        try {
            const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            const isAvailable = response.ok;
            setBackendStatus({
                available: isAvailable,
                lastChecked: new Date()
            });

            // Update global status
            window.backendAvailable = isAvailable;

            toast({
                title: isAvailable ? "Backend is available" : "Backend is unavailable",
                description: isAvailable
                    ? "Connection to backend server successful"
                    : "Could not connect to backend server. Using localStorage fallback.",
                variant: isAvailable ? "default" : "destructive",
            });

        } catch (error) {
            console.error('Error checking backend health:', error);
            setBackendStatus({
                available: false,
                lastChecked: new Date()
            });

            // Update global status
            window.backendAvailable = false;

            toast({
                title: "Backend connection error",
                description: `Error connecting to backend: ${error.message}`,
                variant: "destructive",
            });
        } finally {
            setIsCheckingBackend(false);
        }
    };

    // Check backend status on component mount
    useEffect(() => {
        // Check if we already have a global status
        if (typeof window.backendAvailable !== 'undefined') {
            setBackendStatus({
                available: window.backendAvailable,
                lastChecked: new Date()
            });
        } else {
            checkBackendHealth();
        }
    }, []);

    const testApiConnection = async () => {
        try {
            console.log('Testing API Connection to:', API_BASE_URL);
            const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health`);

            if (response.ok) {
                const data = await response.json();
                toast({
                    title: 'API Connection Success',
                    description: `Backend is healthy: ${JSON.stringify(data)}`,
                    variant: 'success',
                });
                console.log('API health check successful:', data);
                window.backendAvailable = true;
            } else {
                toast({
                    title: 'API Connection Failed',
                    description: `Error: ${response.status} ${response.statusText}`,
                    variant: 'destructive',
                });
                console.error('API health check failed:', response.status, response.statusText);
                window.backendAvailable = false;
            }
        } catch (error) {
            toast({
                title: 'API Connection Error',
                description: `Error: ${error.message}`,
                variant: 'destructive',
            });
            console.error('API connection error:', error);
            window.backendAvailable = false;
        }
    };

    const testSaveReport = async () => {
        try {
            const testReport = {
                id: `test-report-${Date.now()}`,
                subject: 'Test Report from Debug Page',
                date: new Date().toISOString(),
                createdBy: getCurrentUserEmail() || 'test@example.com',
            };

            console.log('Sending test report to API:', testReport);
            console.log('API URL:', `${API_BASE_URL}/reports`);

            const response = await fetch(`${API_BASE_URL}/reports`, {
                method: 'POST',
                headers: getJsonAuthHeaders(),
                body: JSON.stringify(testReport),
            });

            console.log('API response status:', response.status, response.statusText);

            if (response.ok) {
                const data = await response.json();
                toast({
                    title: 'Test Report Saved',
                    description: `Success: ${JSON.stringify(data)}`,
                    variant: 'success',
                });
                console.log('Test report saved successfully:', data);
            } else {
                const errorText = await response.text();
                toast({
                    title: 'Save Report Failed',
                    description: `Error: ${response.status} ${response.statusText}`,
                    variant: 'destructive',
                });
                console.error('Save report failed:', errorText);
            }
        } catch (error) {
            toast({
                title: 'Save Report Error',
                description: `Error: ${error.message}`,
                variant: 'destructive',
            });
            console.error('Save report error:', error);
        }
    };

    // Helper function to get current user email
    function getCurrentUserEmail(): string | null {
        try {
            const userJson = storage.getItem('auth_user');
            if (userJson) {
                const user = JSON.parse(userJson);
                return user.email || null;
            }
        } catch (error) {
            console.error('Error getting user email:', error);
        }
        return null;
    }

    return (
        <div className="container mx-auto py-8 px-4">
            <h1 className="text-3xl font-bold mb-8">Debug & Diagnostics</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center">
                            <GithubIcon className="mr-2 h-5 w-5" />
                            Application Info
                        </CardTitle>
                        <CardDescription>Current application state and environment</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div>
                                <h3 className="font-medium">Environment</h3>
                                <p className="text-sm text-muted-foreground">Mode: {import.meta.env.MODE}</p>
                                <p className="text-sm text-muted-foreground">Development: {String(import.meta.env.DEV)}</p>
                                <p className="text-sm text-muted-foreground">Production: {String(import.meta.env.PROD)}</p>
                                <p className="text-sm text-muted-foreground">Browser Environment: {String(isBrowser)}</p>
                            </div>

                            <div>
                                <h3 className="font-medium">Current User</h3>
                                <p className="text-sm text-muted-foreground">
                                    {user ? (
                                        <>
                                            Logged in as {user.name || user.email} ({user.email}) with role {user.role}
                                        </>
                                    ) : (
                                        'Not logged in'
                                    )}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center">
                            <ServerIcon className="mr-2 h-5 w-5" />
                            Backend Status
                        </CardTitle>
                        <CardDescription>Backend server connection status</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div>
                                <h3 className="font-medium">Connection Status</h3>
                                <p className="text-sm text-muted-foreground">
                                    Backend Available:
                                    <span className={`ml-2 ${backendStatus.available ? 'text-green-500' : 'text-red-500'} font-medium`}>
                                        {backendStatus.available ? 'Yes' : 'No'}
                                    </span>
                                </p>
                                {backendStatus.lastChecked && (
                                    <p className="text-sm text-muted-foreground">
                                        Last Checked: {backendStatus.lastChecked.toLocaleTimeString()}
                                    </p>
                                )}
                            </div>

                            <div>
                                <h3 className="font-medium">Backend Configuration</h3>
                                <p className="text-sm text-muted-foreground">
                                    API URL: {API_BASE_URL}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    LocalStorage Fallback: {String(ENABLE_LOCAL_FALLBACK)}
                                </p>
                            </div>

                            <Button
                                size="sm"
                                onClick={checkBackendHealth}
                                disabled={isCheckingBackend}
                                className="w-full"
                            >
                                {isCheckingBackend ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Checking...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Check Backend Health
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center">
                            <InboxIcon className="mr-2 h-5 w-5" />
                            Storage Configuration
                        </CardTitle>
                        <CardDescription>Storage settings and file path information</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="overflow-x-auto">
                                <table className="table w-full">
                                    <thead>
                                        <tr>
                                            <th>Key</th>
                                            <th>Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {storageItems.map(({ key, value }) => (
                                            <tr key={key}>
                                                <td>{key}</td>
                                                <td>
                                                    <pre className="whitespace-pre-wrap">
                                                        {JSON.stringify(value, null, 2)}
                                                    </pre>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex space-x-2">
                                <Button onClick={handleClearStorage} variant="destructive">
                                    Clear Storage
                                </Button>
                                <Button onClick={handleExportData}>
                                    <DownloadCloud className="mr-2 h-4 w-4" />
                                    Export Data
                                </Button>
                                <Button onClick={handleImportClick}>
                                    <UploadCloud className="mr-2 h-4 w-4" />
                                    Import Data
                                </Button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    accept=".json"
                                    className="hidden"
                                />
                            </div>

                            {isImporting && (
                                <div className="space-y-2">
                                    <Progress value={importProgress} />
                                    <p className="text-sm text-gray-500">Importing... {importProgress.toFixed(0)}%</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>API Connection Tests</CardTitle>
                    <CardDescription>Test direct connections to the backend API</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-2">
                        <Button onClick={testApiConnection} variant="outline">
                            Test API Health
                        </Button>
                        <Button onClick={testSaveReport} variant="outline">
                            Test Save Report
                        </Button>
                    </div>
                    <div className="text-sm space-y-2">
                        <p><strong>API URL:</strong> {API_BASE_URL}</p>
                        <p><strong>Backend Available:</strong> {window.backendAvailable ? 'Yes' : 'No'}</p>
                        <p><strong>Local Fallback:</strong> {ENABLE_LOCAL_FALLBACK ? 'Enabled' : 'Disabled'}</p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Debug; 